// Sync-required: matching backend file at
// quotebid-backend/src/metros.ts.
// If you change either list, change both.
//
// City names are BARE (no ", ST" suffix) to match the
// category_city_caps storage format. Display callers that want
// "City, ST" should append state at render time.
//
// At launch (May 2026), only Baton Rouge metro is open.
// Validation of service_cities is server-side authoritative
// (see backend validateServiceCities); this file mirrors the
// data + read-only helpers for client-side UI rendering only.

export const BATON_ROUGE_METRO = [
  'Baton Rouge',
  'Central',
  'Baker',
  'Zachary',
  'Denham Springs',
  'Walker',
  'Livingston',
  'Watson',
  'Gonzales',
  'Prairieville',
  'Sorrento',
  'Port Allen',
  'Brusly',
  'Addis',
  'Plaquemine',
] as const;

// Cap-provisioned expansion cities (CAPX statewide rows). Mirrored from
// backend src/metros.ts — sync-required: if you change either list, change
// both. These are NOT open metros: registration/service-city validation
// still rejects them. Display-only, for the pricing spot-availability
// selector.
export const STATEWIDE_CAP_CITIES = [
  'Covington',
  'Hammond',
  'Lafayette',
  'Lake Charles',
  'Mandeville',
  'Metairie',
  'Monroe',
  'New Orleans',
  'Shreveport',
  'Slidell',
] as const;

export const OPEN_METROS = {
  'Baton Rouge': BATON_ROUGE_METRO,
} as const;

export type MetroName = keyof typeof OPEN_METROS;
export type City = typeof BATON_ROUGE_METRO[number];

export const ALL_OPEN_CITIES: ReadonlySet<string> = new Set(
  Object.values(OPEN_METROS).flat()
);

const CITY_TO_METRO: ReadonlyMap<string, MetroName> = (() => {
  const m = new Map<string, MetroName>();
  for (const [metroName, cities] of Object.entries(OPEN_METROS) as [MetroName, readonly string[]][]) {
    for (const city of cities) m.set(city, metroName);
  }
  return m;
})();

export function cityToMetro(city: string): MetroName | null {
  return CITY_TO_METRO.get(city) ?? null;
}

export function isCityInOpenMetro(city: string): boolean {
  return ALL_OPEN_CITIES.has(city);
}

export function isMetroOpen(metroName: string): boolean {
  return metroName in OPEN_METROS;
}

export const MAX_SERVICE_CITIES = 50;
